import { Link, useNavigate } from '@tanstack/react-router';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'src/components/ui/table';
import { Badge } from 'src/components/ui/badge';
import { Button } from 'src/components/ui/button';
import { AlertContainer, AlertDescription } from 'src/components/ui/alert';
import {
  CircleNotchIcon,
  WarningCircleIcon,
  ArrowsClockwiseIcon,
} from '@phosphor-icons/react';
import paths from 'src/paths';
import { useListWorkflowDefinitionSummaries } from 'src/api/rpc/hooks/workflows/useWorkflowDefinitions';
import { formatDistanceToNow } from 'date-fns';

export function WorkflowDefinitionsTab() {
  const navigate = useNavigate();
  const {
    data: summariesData,
    isLoading,
    error,
    refetch,
  } = useListWorkflowDefinitionSummaries();

  const workflows = summariesData?.workflows || [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <CircleNotchIcon className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-12">
        <AlertContainer variant="destructive">
          <WarningCircleIcon className="h-4 w-4" />
          <AlertDescription>
            <div className="flex flex-col gap-3">
              <p>
                {error instanceof Error
                  ? error.message
                  : 'Failed to load workflow definitions'}
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => refetch()}
                className="w-fit"
              >
                <ArrowsClockwiseIcon className="h-4 w-4 mr-2" />
                Retry
              </Button>
            </div>
          </AlertDescription>
        </AlertContainer>
      </div>
    );
  }

  if (workflows.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <p>No workflow definitions found.</p>
        <p className="text-sm mt-2">
          Create one from a template or use the button above to start from
          scratch.
        </p>
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Name</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Nodes</TableHead>
          <TableHead>Last Run</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {workflows.map((workflow) => (
          <TableRow
            key={workflow.id}
            className="cursor-pointer hover:bg-muted/50"
            onClick={() => {
              navigate({
                to: paths.authenticated.workflows.definition,
                params: { workflowDefinitionId: workflow.id },
              });
            }}
          >
            <TableCell>
              <Link
                to={paths.authenticated.workflows.definition}
                params={{ workflowDefinitionId: workflow.id }}
                className="font-medium hover:underline"
              >
                {workflow.name}
              </Link>
              {workflow.description && (
                <p className="text-sm text-muted-foreground mt-1">
                  {workflow.description}
                </p>
              )}
            </TableCell>
            <TableCell>
              <Badge variant={workflow.isActive ? 'default' : 'secondary'}>
                {workflow.isActive ? 'Active' : 'Inactive'}
              </Badge>
            </TableCell>
            <TableCell>{workflow.nodeCount}</TableCell>
            <TableCell>
              {workflow.lastRun ? (
                <div className="flex flex-col gap-1">
                  <span className="text-sm">
                    {formatDistanceToNow(
                      new Date(workflow.lastRun.triggeredAt),
                      { addSuffix: true },
                    )}
                  </span>
                  <Badge
                    variant={
                      workflow.lastRun.status === 'completed'
                        ? 'default'
                        : workflow.lastRun.status === 'failed'
                          ? 'destructive'
                          : 'secondary'
                    }
                    className="w-fit"
                  >
                    {workflow.lastRun.status}
                  </Badge>
                </div>
              ) : (
                <span className="text-sm text-muted-foreground">Never run</span>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
